import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Skeleton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  Folder,
  Business,
  Category,
  Build,
  Settings,
  Badge,
  FlightTakeoff,
  Science,
  Inventory2,
  Apartment
} from '@mui/icons-material';
import axios from 'axios';

const SummaryCard = ({ title, value, icon: Icon, color = 'primary' }) => (
  <Card sx={{ height: '100%', borderRadius: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {value}
          </Typography>
        </Box>
        {Icon && (
          <Box sx={{ bgcolor: `${color}.light`, color: `${color}.main`, p: 1.5, borderRadius: 2 }}>
            <Icon sx={{ fontSize: 28 }} />
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const ITEMS = [
  { key: 'ataChapters', label: 'ATA Chapters', icon: Folder, color: 'primary' },
  { key: 'manufacturerNames', label: 'Manufacturer Names', icon: Business, color: 'secondary' },
  { key: 'manufacturerCategories', label: 'Aircraft Categories', icon: Category, color: 'info' },
  { key: 'primaryModels', label: 'Primary Models', icon: FlightTakeoff, color: 'success' },
  { key: 'modelVariants', label: 'Model Variants', icon: FlightTakeoff, color: 'success' },
  { key: 'assemblyTypes', label: 'Assembly Types', icon: Build, color: 'warning' },
  { key: 'assemblyTypeConfigurations', label: 'Assembly Type Configurations', icon: Settings, color: 'warning' },
  { key: 'licenses', label: 'Licenses', icon: Badge, color: 'info' },
  { key: 'agencies', label: 'Agencies', icon: Business, color: 'secondary' },
  { key: 'operators', label: 'Operators', icon: Apartment, color: 'secondary' },
  { key: 'monitorTypes', label: 'Monitor Types', icon: Science, color: 'primary' },
  { key: 'partCategories', label: 'Part Categories', icon: Inventory2, color: 'info' }
];

/**
 * Master Data Dashboard – summary counts for all master data types
 * and a dependency map between key master data entities.
 */
const MasterDataDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [graph, setGraph] = useState(null);
  const [graphLoading, setGraphLoading] = useState(true);
  const [graphError, setGraphError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get('/api/master-data/summary');
        if (!cancelled) setSummary(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load summary');
          setSummary(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSummary();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchGraph = async () => {
      setGraphLoading(true);
      setGraphError(null);
      try {
        const res = await axios.get('/api/master-data/dependency-graph');
        if (!cancelled) setGraph(res.data);
      } catch (err) {
        if (!cancelled) {
          setGraphError(err.response?.data?.message || 'Failed to load dependency graph');
          setGraph(null);
        }
      } finally {
        if (!cancelled) setGraphLoading(false);
      }
    };
    fetchGraph();
    return () => { cancelled = true; };
  }, []);

  if (loading && !summary) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rounded" height={100} />
          </Grid>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={90} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const data = summary || {};
  const total = data.total ?? 0;

  const relationCounts = (graph?.edges || []).reduce((acc, edge) => {
    acc[edge.relation] = (acc[edge.relation] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom color="text.secondary">
        Overview
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Total master data records</Typography>
              <Typography variant="h3" fontWeight={700}>{total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {ITEMS.map(({ key, label, icon: Icon, color: c }) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <SummaryCard title={label} value={data[key] ?? 0} icon={Icon} color={c} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
        By type
      </Typography>
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <List dense disablePadding>
            {ITEMS.map(({ key, label }) => {
              const count = data[key] ?? 0;
              const max = Math.max(...ITEMS.map((i) => data[i.key] ?? 0), 1);
              return (
                <ListItem key={key} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={label}
                    secondary={
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, (count / max) * 100)}
                        sx={{ mt: 0.5, height: 6, borderRadius: 1, bgcolor: 'action.hover' }}
                        color="primary"
                      />
                    }
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  <Chip size="small" label={count} sx={{ ml: 1 }} />
                </ListItem>
              );
            })}
          </List>
        </CardContent>
      </Card>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
        Dependency map
      </Typography>
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          {graphLoading && !graph && !graphError && (
            <Box>
              <Skeleton variant="rounded" height={32} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={32} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={32} />
            </Box>
          )}
          {graphError && (
            <Typography color="error" variant="body2">
              {graphError}
            </Typography>
          )}
          {!graphLoading && graph && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Visual relationships between master data entities, based on implemented schema.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Source
                  </Typography>
                  <List dense disablePadding>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText primary="ATA Chapters" primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText primary="Manufacturer Names" primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText primary="Aircraft Categories" primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText primary="Part Categories (root)" primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Relationship
                  </Typography>
                  <List dense disablePadding>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="ATA Chapters → Assembly Types → Assembly Type Configurations"
                        secondary={`Links: ${relationCounts.ATA_CHAPTER_TO_ASSEMBLY_TYPE || 0} ATA→Assembly, ${relationCounts.ASSEMBLY_TYPE_TO_CONFIGURATION || 0} Assembly→Config`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Manufacturer Names → Models (Primary & Variants)"
                        secondary={`Links: ${relationCounts.MANUFACTURER_NAME_TO_MODEL || 0}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Primary Models → Model Variants"
                        secondary={`Links: ${relationCounts.PRIMARY_MODEL_TO_VARIANT || 0}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Aircraft Categories → Models"
                        secondary={`Links: ${relationCounts.AIRCRAFT_CATEGORY_TO_MODEL || 0}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Part Category parent → child"
                        secondary={`Links: ${relationCounts.PART_CATEGORY_PARENT_TO_CHILD || 0}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    This map shows how master data drives Aircraft Life-cycle screens:
                  </Typography>
                  <List dense disablePadding>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="ATA & Assembly Types"
                        secondary="Control which assemblies exist per aircraft and how their Time Since columns are configured."
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Manufacturer / Category / Models"
                        secondary="Define aircraft family trees used in Aircraft, Assemblies, and Components."
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary="Part Categories"
                        secondary="Provide a hierarchical grouping for parts, used in Parts Catalog and lifecycle."
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MasterDataDashboard;
